import React, { useState } from "react";
import axios from "axios";
import {
  Pencil,
  PlusCircle,
  Trash2,
  X,
} from "lucide-react";

import { useCases } from "../UI/components/Context/CasesContext";

import "./ManageCases.css";

const API = process.env.REACT_APP_API_URL;

const initialState = {
  title: "",
  category: "",
  description: "",
  goalAmount: "",
  amountCollected: 0,
  quickAmounts: "250,500,1000,2500",
  beneficiaries: "",
  status: "Active",
  image: null,
};

const CasesTable = () => {
  const { cases, setCases } = useCases();

  const [searchTerm, setSearchTerm] =
    useState("");

  const [showForm, setShowForm] =
    useState(false);

  const [newCase, setNewCase] = useState({
    ...initialState,
  });

  const [errors, setErrors] = useState({});

  const [editingIndex, setEditingIndex] =
    useState(null);

  const token =
    sessionStorage.getItem("token");

  /* ======================================
      INPUT CHANGE
  ====================================== */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setNewCase((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  /* ======================================
      FILE CHANGE
  ====================================== */

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      setNewCase((prev) => ({
        ...prev,
        image: file,
      }));
    }
  };

  /* ======================================
      VALIDATION
  ====================================== */

  const validateForm = () => {
    let tempErrors = {};

    if (!newCase.title.trim()) {
      tempErrors.title =
        "Campaign title required";
    }

    if (!newCase.category) {
      tempErrors.category =
        "Category required";
    }

    if (
      !newCase.goalAmount ||
      newCase.goalAmount <= 0
    ) {
      tempErrors.goalAmount =
        "Goal amount required";
    }

    if (!newCase.description.trim()) {
      tempErrors.description =
        "Description required";
    }

    if (
      !newCase.image &&
      editingIndex === null
    ) {
      tempErrors.image =
        "Campaign image required";
    }

    return tempErrors;
  };

  /* ======================================
      ADD / UPDATE CAMPAIGN
  ====================================== */

  const handleSubmit = async () => {
    const validationErrors = validateForm();

    if (
      Object.keys(validationErrors).length > 0
    ) {
      return setErrors(validationErrors);
    }

    try {
      const formattedCase = {
        ...newCase,

        quickAmounts:
          newCase.quickAmounts
            .split(",")
            .map((n) =>
              Number(n.trim())
            ),
      };

      const formData = new FormData();

      Object.keys(formattedCase).forEach(
        (key) => {
          if (
            key === "quickAmounts"
          ) {
            formData.append(
              key,
              JSON.stringify(
                formattedCase[key]
              )
            );
          } else if (
            key === "image"
          ) {
            if (
              formattedCase.image instanceof
              File
            ) {
              formData.append(
                "image",
                formattedCase.image
              );
            }
          } else {
            formData.append(
              key,
              formattedCase[key]
            );
          }
        }
      );

      let response;

      /* UPDATE */

      if (editingIndex !== null) {
        response = await axios.put(
          `${API}/api/cases/${newCase._id}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type":
                "multipart/form-data",
            },
          }
        );

        const updatedCases = [...cases];

        updatedCases[editingIndex] =
          response.data;

        setCases(updatedCases);

        setEditingIndex(null);
      }

      /* CREATE */

      else {
        response = await axios.post(
          `${API}/api/cases`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type":
                "multipart/form-data",
            },
          }
        );

        setCases([
          response.data,
          ...cases,
        ]);
      }

      /* RESET */

      setNewCase({
        ...initialState,
      });

      setErrors({});

      setShowForm(false);
    } catch (err) {
      console.error(err);

      alert(
        "❌ Failed to save campaign"
      );
    }
  };

  /* ======================================
      EDIT CAMPAIGN
  ====================================== */

  const handleEditCase = (index) => {
    const selectedCase = cases[index];

    setNewCase({
      title:
        selectedCase.title || "",

      category:
        selectedCase.category ||
        "Other",

      description:
        selectedCase.description ||
        "",

      goalAmount:
        selectedCase.goalAmount ||
        "",

      amountCollected:
        selectedCase.amountCollected ||
        0,

      quickAmounts:
        selectedCase.quickAmounts?.join(
          ","
        ) ||
        "250,500,1000,2500",

      beneficiaries:
        selectedCase.beneficiaries ||
        "",

      status:
        selectedCase.status ||
        "Active",

      image: null,

      _id: selectedCase._id,
    });

    setEditingIndex(index);

    setShowForm(true);
  };

  /* ======================================
      DELETE CAMPAIGN
  ====================================== */

  const handleDelete = async (id) => {
    const confirmDelete =
      window.confirm(
        "Delete this campaign?"
      );

    if (!confirmDelete) return;

    try {
      await axios.delete(
        `${API}/api/cases/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCases(
        cases.filter(
          (c) => c._id !== id
        )
      );
    } catch (err) {
      console.error(err);

      alert(
        "❌ Failed to delete campaign"
      );
    }
  };

  /* ======================================
      STATUS CHANGE
  ====================================== */

  const handleStatusChange = async (
    index,
    newStatus
  ) => {
    try {
      const selectedCase =
        cases[index];

      const response = await axios.put(
        `${API}/api/cases/${selectedCase._id}`,
        {
          ...selectedCase,
          status: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updatedCases = [...cases];

      updatedCases[index] =
        response.data;

      setCases(updatedCases);
    } catch (error) {
      console.error(error);

      alert(
        "❌ Failed to update status"
      );
    }
  };

  /* ======================================
      SEARCH
  ====================================== */

  const filteredCases = cases.filter(
    (c) =>
      c.title
        ?.toLowerCase()
        .includes(
          searchTerm.toLowerCase()
        )
  );

  return (
    <div id="cases-container">
      {/* TOPBAR */}

      <div id="cases-topbar">
        <h2>Campaign Management</h2>

        <div id="cases-actions">
          <input
            type="text"
            placeholder="Search campaign..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(
                e.target.value
              )
            }
          />

          <button
            onClick={() => {
              setEditingIndex(null);

              setNewCase({
                ...initialState,
              });

              setShowForm(true);
            }}
            id="add-btn"
          >
            <PlusCircle className="inline w-5 h-5 mr-1" />

            Add Campaign
          </button>
        </div>
      </div>

      {/* TABLE */}

      <div id="cases-table-section">
        <table id="cases-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Campaign</th>
              <th>Category</th>
              <th>Goal</th>
              <th>Raised</th>
              <th>Status</th>
              <th>Image</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredCases.map(
              (c, index) => (
                <tr key={c._id}>
                  <td>{index + 1}</td>

                  <td>{c.title}</td>

                  <td>{c.category}</td>

                  <td>
                    ₹
                    {c.goalAmount?.toLocaleString()}
                  </td>

                  <td>
                    ₹
                    {c.amountCollected?.toLocaleString()}
                  </td>

                  <td>
                    <select
                      value={c.status}
                      onChange={(e) =>
                        handleStatusChange(
                          index,
                          e.target.value
                        )
                      }
                    >
                      <option value="Active">
                        Active
                      </option>

                      <option value="Inactive">
                        Inactive
                      </option>
                    </select>
                  </td>

                  <td>
                    {c.image ? (
                      <img
                        src={`${API}${c.image}`}
                        alt="campaign"
                        id="case-img"
                      />
                    ) : (
                      "N/A"
                    )}
                  </td>

                  <td id="case-actions">
                    <button
                      onClick={() =>
                        handleEditCase(
                          index
                        )
                      }
                      id="edit-btn"
                    >
                      <Pencil className="w-5 h-5" />
                    </button>

                    <button
                      onClick={() =>
                        handleDelete(
                          c._id
                        )
                      }
                      id="close-btn"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      {/* FORM */}

      {showForm && (
        <div id="cases-form-container">
          <div id="cases-form">
            <div id="form-header">
              <h3>
                {editingIndex !== null
                  ? "Update Campaign"
                  : "Add Campaign"}
              </h3>

              <X
                id="form-close"
                onClick={() =>
                  setShowForm(false)
                }
              />
            </div>

            <input
              type="text"
              name="title"
              placeholder="Campaign Title"
              value={newCase.title}
              onChange={handleChange}
            />

            {errors.title && (
              <p className="error">
                {errors.title}
              </p>
            )}

            <select
              name="category"
              value={newCase.category}
              onChange={handleChange}
            >
              <option value="">
                Select Category
              </option>

              <option value="Food">
                Food
              </option>

              <option value="Medical">
                Medical
              </option>

              <option value="Education">
                Education
              </option>

              <option value="Women Empowerment">
                Women Empowerment
              </option>

              <option value="Child Care">
                Child Care
              </option>

              <option value="Old Age Support">
                Old Age Support
              </option>

              <option value="Animal Care">
                Animal Care
              </option>

              <option value="Disaster Relief">
                Disaster Relief
              </option>

              <option value="Other">
                Other
              </option>
            </select>

            <input
              type="number"
              name="goalAmount"
              placeholder="Goal Amount"
              value={newCase.goalAmount}
              onChange={handleChange}
            />

            <input
              type="text"
              name="beneficiaries"
              placeholder="Beneficiaries"
              value={
                newCase.beneficiaries
              }
              onChange={handleChange}
            />

            <input
              type="text"
              name="quickAmounts"
              placeholder="250,500,1000"
              value={
                newCase.quickAmounts
              }
              onChange={handleChange}
            />

            <input
              type="file"
              onChange={
                handleFileChange
              }
            />

            <textarea
              name="description"
              placeholder="Campaign Description"
              value={
                newCase.description
              }
              onChange={handleChange}
            />

            <div id="form-buttons">
              <button
                id="cancel-btn"
                onClick={() =>
                  setShowForm(false)
                }
              >
                Cancel
              </button>

              <button
                id="submit-btn"
                onClick={handleSubmit}
              >
                {editingIndex !== null
                  ? "Update"
                  : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CasesTable;